import { AlertTriangle, Menu, Moon, PanelLeftClose, PanelLeftOpen, Snowflake, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { THEMES, useApp } from '../context/AppContext'
import type { Theme } from '../lib/types'
import { cn } from '../lib/utils'
import { Badge, Button } from './ui'

const THEME_ICON: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  lapland: Snowflake,
}

const THEME_LABEL: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  lapland: 'Lapland',
}

const LANG_LABEL: Record<string, string> = { fi: 'FI', en: 'EN', es: 'ES' }

interface HeaderProps {
  onOpenMenu: () => void
  sidebarCollapsed?: boolean
  onToggleSidebar?: () => void
}

/**
 * Cabecera superior sticky y ligera
 */
export function Header({ onOpenMenu, sidebarCollapsed, onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation()
  const { locale, setLocale, theme, setTheme } = useApp()

  return (
    <header className="bg-background/85 sticky top-0 z-40 border-b backdrop-blur-md">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex items-center gap-2">
          {/* Botón hamburguesa móvil */}
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label={t('common.menu')}
            className="hover:bg-accent rounded-md p-1.5 md:hidden"
          >
            <Menu className="size-5" />
          </button>

          {/* Toggle de sidebar desktop en header */}
          {onToggleSidebar ? (
            <button
              type="button"
              onClick={onToggleSidebar}
              title={sidebarCollapsed ? 'Expandir menú lateral' : 'Ocultar menú lateral'}
              className="hidden md:flex hover:bg-accent rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="size-4" />
              ) : (
                <PanelLeftClose className="size-4" />
              )}
            </button>
          ) : null}

          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs font-semibold tracking-tight text-foreground/80">
              DEPLOYTOUR Pilot 5
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span className="text-xs text-muted-foreground">Visit Levi</span>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2.5">
          {/* Selector de idioma con FI primero */}
          <div className="bg-muted/80 flex rounded-lg p-0.5 border" role="group" aria-label={t('common.language')}>
            {(['fi', 'en', 'es'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLocale(l)}
                aria-pressed={locale === l}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition-all',
                  locale === l ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                )}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>

          {/* Selector de temas (Light / Dark / Lapland) */}
          <div className="flex items-center gap-1 bg-muted/60 p-0.5 rounded-lg border" role="group" aria-label={t('common.theme')}>
            {THEMES.map((th) => {
              const Icon = THEME_ICON[th]
              return (
                <Button
                  key={th}
                  size="icon"
                  variant={theme === th ? 'default' : 'ghost'}
                  className="size-7 rounded-md"
                  onClick={() => setTheme(th)}
                  title={THEME_LABEL[th]}
                  aria-pressed={theme === th}
                >
                  <Icon className="size-3.5" />
                </Button>
              )
            })}
          </div>
        </div>
      </div>
    </header>
  )
}

/**
 * Footer ligero y sticky con notice de procedencia y datos
 */
export function PolicyBanner() {
  const { t } = useTranslation()
  const { audience } = useApp()
  return (
    <footer className="sticky bottom-0 z-40 border-t bg-background/90 px-4 py-2.5 backdrop-blur-md">
      <div className="text-muted-foreground mx-auto flex max-w-[1680px] flex-wrap items-center justify-between gap-3 text-xs 2xl:px-2">
        <p className="flex items-center gap-2">
          <AlertTriangle className="text-amber-500 size-3.5 shrink-0" />
          <span className="line-clamp-1">{t('policy.simulatedNotice')}</span>
        </p>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] font-mono uppercase">
            {audience === 'public' ? 'Public View' : 'Internal / SME'}
          </Badge>
          <span className="text-[11px] text-muted-foreground/80">
            DEPLOYTOUR · AnySolution
          </span>
        </div>
      </div>
    </footer>
  )
}